import AspectRatioSelect, { storyboardRatio } from "./AspectRatioSelect"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { canAccessSceneStudio } from "../../utils/access"
import { Button, Input, Modal, Select } from "antd"
import SubscriptionPreview from "../MyProfile/Subscription/SubscriptionPreview"
import { MdAutoAwesome, MdLightbulb, MdMovie, MdInsights, MdViewQuilt, MdVideocam, MdLocalMovies, MdTune, MdArrowBack, MdTheaters, MdMovieFilter, MdRefresh } from "react-icons/md"
import { aiTools } from "./aiTools"
import { loadUserScripts } from "./loadUserScripts"
import { loadScriptScenes } from "./loadScriptScenes"
import { runAiTool, getStandardBalance, claimCreditWarning, getScriptPages } from "./aiApi"
import StandardCredits from "./StandardCredits"
import { runStoryboard, openSavedStoryboard } from "./storyboardApi"
import { prepareMedia } from "./mediaContext"
import AIVideo from "./AIVideo"
import PremiumVideo from "./PremiumVideo"
import SceneStudio from "./SceneStudio"
import useSubscriptionAccess from '../../utils/useSubscriptionAccess'
import ProductionElements from "./ProductionElements"
import StoryboardSequence, { StoryboardSetup, SavedStoryboards, CustomiseShots, defaultStoryboardShots } from "./StoryboardSequence"
import StoryboardContentGuidelines, { StoryboardGuidelinesLink, StoryboardFailureNotice } from "./StoryboardContentGuidelines"

const icons = { ideas: MdLightbulb, scene: MdMovie, script: MdInsights, storyboard: MdViewQuilt, video: MdVideocam, premium: MdLocalMovies, production: MdTheaters, "scene-studio": MdMovieFilter }
const costText = (t, cost) => `${cost ?? t.cost} ${t.costLabel}`

// Minimal, safe formatting for AI answers: headings, bullets and bold (no HTML injection).
const inline = (text) => text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part)
const AnswerText = ({ text }) => {
  const out = []
  let list = []
  const flush = () => { if (list.length) { out.push(<ul key={`l${out.length}`} className="list-disc pl-5 my-2 space-y-1">{list}</ul>); list = [] } }
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim()
    const bullet = /^[-*•]\s+(.*)/.exec(line) || /^\d+[.)]\s+(.*)/.exec(line)
    if (bullet) { list.push(<li key={i}>{inline(bullet[1])}</li>); return }
    flush()
    const h = /^#{1,6}\s+(.*)/.exec(line)
    if (h) out.push(<h4 key={i} className="font-bold text-[16px] mt-4 mb-1">{inline(h[1])}</h4>)
    else if (line) out.push(<p key={i} className="my-2">{inline(line)}</p>)
  })
  flush()
  return <div className="ai-answer text-[15px] leading-relaxed">{out}</div>
}

const AIWorkspace = ({ fixedScript, toolIds, initialTool, beforeGenerate, onCharacters }) => {
  const subscriptionAccess=useSubscriptionAccess()
  const [balance, setBalance] = useState(null)
  const [warning, setWarning] = useState(null)
  const location = useLocation()
  const returnState = location.state?.subscriptionReturn
  const [subscriptionOpen, setSubscriptionOpen] = useState(Boolean(returnState))
  const creditFetch = useRef(false)
  const mounted = useRef(false)
  const refreshBalance = useCallback(async () => {
    if (creditFetch.current) return
    creditFetch.current = true
    try {
      const next = await getStandardBalance()
      if (!mounted.current) return
      setBalance(next)
      window.dispatchEvent(new CustomEvent('morphosys:ai-balance', { detail: next }))
      if (!next.realBalance || next.remaining > 10) setWarning(null)
      // Claim/rearm on the server, never from the trial placeholder.
      if (next.realBalance) {
        const event = await claimCreditWarning()
        if (mounted.current && event.showWarning && Number.isFinite(event.remaining)) setWarning(event.remaining)
      }
    } catch (e) {
      if (mounted.current) setBalance(null)
    } finally { creditFetch.current = false }
  }, [])
  useEffect(() => {
    mounted.current = true
    refreshBalance()
    const onFocus = () => refreshBalance()
    window.addEventListener("focus", onFocus)
    return () => { mounted.current = false; window.removeEventListener("focus", onFocus) }
  }, [refreshBalance])
  const user = useSelector((state) => state.auth.user) || JSON.parse(localStorage.getItem("user") || "null")
  // Tools with an access rule are listed only for users who pass it; the server enforces it too.
  const menuTools = aiTools.filter((t) => (!toolIds || toolIds.includes(t.id)) && (t.access !== "sceneStudio" || canAccessSceneStudio(user)))
  const [toolId, setToolId] = useState(initialTool || (aiTools.some((t) => t.id === returnState?.tool) ? returnState.tool : aiTools[0].id))
  const [stage, setStage] = useState("setup") // "setup" | "result"
  const [selected, setSelected] = useState({})
  const [notes, setNotes] = useState("")
  const [scripts, setScripts] = useState(fixedScript ? [fixedScript] : [])
  const [scriptsLoading, setScriptsLoading] = useState(false)
  const [scriptId, setScriptId] = useState(fixedScript?._id)
  const [scenes, setScenes] = useState([])
  const [scenesLoading, setScenesLoading] = useState(false)
  const [sceneId, setSceneId] = useState()
  const [pageInfo, setPageInfo] = useState(null) // { pages, category, credits } or { pages: null, message } from the server
  const [result, setResult] = useState(null)
  const [running, setRunning] = useState(false)
  const [runError, setRunError] = useState("")
  const [guidelinesOpen, setGuidelinesOpen] = useState(false)
  const [runProgress, setRunProgress] = useState("")
  const [wardrobe, setWardrobe] = useState("")
  const [aspectRatio, setAspectRatio] = useState("16:9")
  const [shots, setShots] = useState(defaultStoryboardShots)
  // Set while regenerating a storyboard: { sceneNumber, sceneHeading } of the version being regenerated.
  const [regenFrom, setRegenFrom] = useState(null)
  const [regenNotice, setRegenNotice] = useState("")
  const pendingRegenScene = useRef(null)
  const navigate = useNavigate()
  const tool = aiTools.find((t) => t.id === toolId)
  const picked = selected[toolId] || []
  const script = scripts.find((s) => s._id === scriptId)
  // Script Analysis cost comes from the script's saved page count, as reported by the server.
  const analysisAvailable = !!script && !!pageInfo?.pages
  const effectiveCost = toolId === "script" ? pageInfo?.credits : undefined
  const toolCostText = toolId === "script" && effectiveCost === undefined ? "10–25 credits (by page count)" : costText(tool, effectiveCost)

  // Everything a future AI request will need, kept in one place.
  const request = { tool: toolId, scriptId, sceneId, options: picked, notes, aspectRatio: toolId === "storyboard" ? aspectRatio : undefined, sourceStoryboardId: toolId === "storyboard" ? regenFrom?.usageId : undefined, wardrobe: toolId === "storyboard" ? wardrobe : undefined, shots: toolId === "storyboard" ? shots.map((s) => ({ shot: s.shot, camera: s.camera.trim() })) : undefined }

  const requiredCredits = effectiveCost ?? tool.cost
  const storyboardUpgradeRequired = toolId === "storyboard" && subscriptionAccess.enrolled === true
    && !subscriptionAccess.publicTrial && subscriptionAccess.canEdit === true && subscriptionAccess.canUseAI !== true
  const creditEligible = balance?.mode === "recorded-only-trial" || (balance?.realBalance && Number.isFinite(balance.remaining) && balance.remaining >= requiredCredits)
  const creditMessage = !balance ? "Unable to verify your credit balance. Please try again."
    : !creditEligible && toolId !== "video" ? `You need ${requiredCredits} standard AI credits. You have ${balance.remaining} remaining. Purchasing is not yet available.` : ""
  const ready = subscriptionAccess.canUseAI===true && tool.connected && !running && creditEligible
    && (!tool.needsScript || !!scriptId)
    && (!tool.needsScene || !!sceneId)
    && (toolId !== "script" || analysisAvailable)
    && (!tool.notesRequired || !!notes.trim())
    && (toolId !== "ideas" || picked.length > 0)
    && (toolId !== "storyboard" || picked.length === 1)

  // Regeneration always shows the normal credit charge and asks for confirmation first.
  const confirmRegenerate = () => new Promise((resolve) => Modal.confirm({
    title: "Regenerate storyboard?",
    content: `This creates all four frames again as a new version and uses ${tool.cost} AI credits. The previous version stays saved.`,
    okText: `Regenerate · ${tool.cost} credits`, cancelText: "Cancel", centered: true,
    onOk: () => resolve(true), onCancel: () => resolve(false),
  }))

  const runTool = async () => {
    if (!ready) return
    if (toolId === "storyboard" && regenFrom && !(await confirmRegenerate())) return
    setRunning(true); setRunError("")
    try {
      if (toolId === "storyboard") {
        await prepareMedia(beforeGenerate, scriptId, sceneId, scenes)
        setResult({ tool: "storyboard", summary, documentId: scriptId, sceneNumber: Number(sceneId), sceneHeading: scenes.find((s) => s.id === sceneId)?.heading, style: picked[0], aspectRatio, wardrobe: wardrobe.trim(), shots: request.shots, frames: [], srcs: [], final: false })
        setRegenFrom(null); setRegenNotice("")
        setStage("result")
        await runStoryboard(request, (event) => {
          if (event.type === "progress") setRunProgress(event.message)
          if (event.type === "plan") setResult((prev) => ({ ...prev, frames: event.frames, characters: event.characters }))
          if (event.type === "plan") setResult((prev) => ({ ...prev, expiresAt: event.expiresAt }))
          if (event.type === "frame") setResult((prev) => { const srcs = [...(prev.srcs || [])]; srcs[event.index] = event.src; return { ...prev, srcs } })
          if (event.type === "complete") setResult((prev) => ({ ...prev, ...event, final: true }))
        })
        setSavedKey((k) => k + 1)
        return
      }
      const data = await runAiTool(request)
      setResult({ tool: toolId, text: data?.answer || "", summary, credits: data?.credits })
      setStage("result")
    } catch (e) {
      setRunError(e.message)
      if (toolId === "storyboard") setResult((prev) => prev ? { ...prev, stopped: true } : prev)
    } finally {
      setRunning(false)
      setRunProgress("")
      refreshBalance()
    }
  }

  const [openingId, setOpeningId] = useState()
  const [savedKey, setSavedKey] = useState(0)
  // Reopens an existing storyboard from its saved record — no regeneration, no credits.
  const openSaved = async (id) => {
    setOpeningId(id); setRunError("")
    try {
      const d = await openSavedStoryboard(id)
      setResult({ ...d, tool: "storyboard", saved: true, final: true, summary: ["Storyboard", d.scriptTitle, [d.sceneNumber && `Scene ${d.sceneNumber}`, d.sceneHeading].filter(Boolean).join(" "), d.style].filter(Boolean).join(" · ") })
      setStage("result")
    } catch (e) { setRunError(e.message) } finally { setOpeningId(undefined) }
  }
  // Back to the setup/saved list; refreshes the saved list (e.g. after a delete).
  const backFromSaved = () => { setResult(null); setSavedKey((k) => k + 1); setStage("setup") }

  useEffect(() => {
    if (fixedScript) return
    let cancelled = false
    setScriptsLoading(true)
    loadUserScripts(() => cancelled).then((s) => !cancelled && setScripts(s)).catch(() => {}).finally(() => !cancelled && setScriptsLoading(false))
    return () => { cancelled = true }
  }, [fixedScript])

  // Page count, category and exact cost for Script Analysis, verified by the server.
  useEffect(() => {
    setPageInfo(null)
    if (!scriptId || toolId !== "script") return
    let cancelled = false
    getScriptPages(scriptId)
      .then((info) => !cancelled && setPageInfo(info))
      .catch((e) => !cancelled && setPageInfo({ pages: null, message: e.message }))
    return () => { cancelled = true }
  }, [scriptId, toolId])

  const scenesFor = useRef(null)
  useEffect(() => {
    setSceneId(undefined); setScenes([]); scenesFor.current = null
    if (!scriptId) return
    let cancelled = false
    setScenesLoading(true)
    loadScriptScenes(scriptId)
      .then((list) => { if (!cancelled) { scenesFor.current = scriptId; setScenes(list) } })
      .catch(() => { if (!cancelled) { scenesFor.current = scriptId; setScenes([]) } })
      .finally(() => !cancelled && setScenesLoading(false))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptId])

  // Reselects the regenerated storyboard's scene once that script's scenes are loaded. Scene numbers are
  // not permanent identities, so the scene is only preselected when its heading still matches the saved one.
  useEffect(() => {
    const pending = pendingRegenScene.current
    if (!pending || scenesFor.current !== pending.scriptId) return
    pendingRegenScene.current = null
    const match = scenes.find((s) => String(s.id) === pending.sceneId)
    const sameHeading = match && (!pending.heading || String(match.heading || "").trim().toUpperCase() === String(pending.heading).trim().toUpperCase())
    if (sameHeading) setSceneId(match.id)
    else setRegenNotice("This scene has changed since the storyboard was saved. Please choose the scene to regenerate.")
  }, [scenes, regenFrom])

  // Loads a storyboard's saved settings into the setup form so the user can adjust them before regenerating.
  const startRegenerate = (source) => {
    if (!source) return
    setRegenNotice("")
    setSelected((prev) => ({ ...prev, storyboard: source.style ? [source.style] : (prev.storyboard || []) }))
    setAspectRatio(storyboardRatio(source))
    setWardrobe(source.wardrobe || "")
    setShots(Array.isArray(source.shots) && source.shots.length === 4 ? source.shots.map((s) => ({ shot: s.shot, camera: s.camera || "" })) : defaultStoryboardShots())
    const targetScript = source.documentId || scriptId
    const sceneKey = source.sceneNumber != null ? String(source.sceneNumber) : source.sceneId != null ? String(source.sceneId) : null
    if (fixedScript && targetScript !== fixedScript._id) { setRunError("This storyboard belongs to a different script."); return }
    pendingRegenScene.current = sceneKey ? { scriptId: targetScript, sceneId: sceneKey, heading: source.sceneHeading } : null
    if (targetScript !== scriptId) setScriptId(targetScript)
    setRegenFrom({ usageId: source.usageId, sceneNumber: source.sceneNumber ?? source.sceneId, sceneHeading: source.sceneHeading })
    setStage("setup")
  }
  const cancelRegenerate = () => { setRegenFrom(null); setRegenNotice(""); pendingRegenScene.current = null; setAspectRatio((ratio) => ratio === "3:2" ? "16:9" : ratio) }


  const toggle = (opt) => setSelected((prev) => {
    const cur = prev[toolId] || []
    const next = tool.single ? (cur.includes(opt) ? [] : [opt]) : cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt]
    return { ...prev, [toolId]: next }
  })

  const chip = (label, active, onClick) => (
    <button key={label} type="button" aria-pressed={active} onClick={onClick} className="ai-chip rounded-full px-4 h-10 text-[14px]">{label}</button>
  )

  const summary = [tool.title, script && (script.title || "Untitled Script"), sceneId && `Scene ${sceneId} ${scenes.find((s) => s.id === sceneId)?.heading || ""}`.trim(), pageInfo?.pages && toolId === "script" && `${pageInfo.pages} pages · ${pageInfo.category}`, picked.join(", ")].filter(Boolean).join(" · ")

  return (
    <div className="ai-workspace rounded-lg border bg-white" style={{ borderColor: "var(--dashboard-border)" }} data-stage={stage}>
      <style>{`
        .ai-workspace .ai-tool[aria-pressed="true"] { background: var(--dashboard-tint); color: var(--dashboard-primary); font-weight: 700; }
        .ai-workspace .dashboard-primary.ant-btn:disabled { opacity: .55; cursor: not-allowed; }
        .ai-workspace .ai-chip { border: 1px solid var(--dashboard-border); color: var(--dashboard-ink); background: #fff; }
        .ai-workspace .ai-chip[aria-pressed="true"] { border-color: var(--dashboard-primary); background: var(--dashboard-tint); color: var(--dashboard-primary); font-weight: 700; }
      `}</style>
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 lg:px-5 border-b" style={{ borderColor: "var(--dashboard-border)" }}>
        <div className="flex items-center gap-2 font-bold text-[17px]"><MdAutoAwesome size={22} color="var(--dashboard-primary)" />AI Workspace</div>
        <StandardCredits balance={balance} warning={warning} onDismiss={() => setWarning(null)} />
      </div>

      {stage === "result" ? (
        <div className="flex flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 lg:px-5 py-3 border-b" style={{ borderColor: "var(--dashboard-border)" }}>
            <p className="dashboard-muted text-[14px] min-w-0 truncate">{result?.summary || summary}</p>
            <div className="flex flex-wrap gap-2">{result?.saved
              ? <Button className="dashboard-tool rounded-full font-bold" icon={<MdArrowBack size={18} />} onClick={backFromSaved}>Back</Button>
              : <Button disabled={running} className="dashboard-tool rounded-full font-bold" icon={<MdTune size={18} />} onClick={() => setStage("setup")}>Edit options</Button>}
            {result?.tool === "storyboard" && result.final && !running && subscriptionAccess.canUseAI === true
              && <Button className="dashboard-primary rounded-full font-bold" icon={<MdRefresh size={18} />} onClick={() => startRegenerate(result)}>Regenerate Storyboard</Button>}</div>
          </div>
          <div className="p-4 lg:p-6 min-h-[360px]">
            {result ? <>
              {result.tool === "storyboard" ? <StoryboardSequence key={result.usageId || "live"} result={result} onBack={backFromSaved} /> : <AnswerText text={result.text} />}
              {running && <p role="status" className="dashboard-muted text-[14px] mt-4">{runProgress || "Preparing storyboard…"}</p>}
              {runError && (result.tool === "storyboard" ? <StoryboardFailureNotice message={runError} onGuidelines={() => setGuidelinesOpen(true)} /> : <p role="alert" className="text-red-600 text-[14px] mt-4">{runError}</p>)}
              <p className="dashboard-muted text-[12px] mt-6">{balance?.deductionsEnabled ? 'Credits are deducted from your available balance.' : 'Credits are not deducted during the trial.'}</p>
            </> : <p className="dashboard-muted text-[14px]">{tool.id === "storyboard" ? "Storyboard frames will appear here." : "Results will appear here."}</p>}
          </div>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row">
          <div className={`p-4 lg:hidden ${menuTools.length === 1 ? 'hidden' : ''}`}>
            <label htmlFor="ai-tool-select" className="block font-bold text-[14px] mb-2">AI tool</label>
            <Select id="ai-tool-select" aria-label="AI tool" size="large" className="w-full" value={toolId} onChange={setToolId}
              options={menuTools.map((t) => ({ value: t.id, label: `${t.title} · ${t.id === "script" ? "10–25 credits" : t.id === "video" ? "10–20 Extra AI Credits" : costText(t)}` }))} />
          </div>
          <nav aria-label="AI tools" className={`${menuTools.length === 1 ? 'hidden' : 'hidden lg:flex'} flex-col gap-1 p-3 w-[230px] shrink-0 border-r`} style={{ borderColor: "var(--dashboard-border)" }}>
            {menuTools.map((t) => { const Icon = icons[t.id]; return (
              <button key={t.id} type="button" aria-pressed={toolId === t.id} onClick={() => setToolId(t.id)} className="ai-tool flex items-center gap-3 rounded-lg px-3 h-11 text-left text-[15px] dashboard-muted">
                <Icon size={20} /><span className="flex-1">{t.title}</span><span className="text-[12px]">{t.cost}</span>
              </button>) })}
          </nav>
          {subscriptionAccess.publicTrial && (subscriptionAccess.canUseAI !== true || subscriptionAccess.trialAI && !['ideas', 'scene', 'script', 'storyboard'].includes(toolId)) ? <div className="flex-1 min-w-0 p-4 lg:p-5 space-y-4">
            <h3 className="font-bold text-lg">{tool.title}</h3><p>{tool.intro}</p>
            <h4 className="font-bold">Explore Morphosys AI</h4>
            <p>Discover how AI can support screenplay development, creative planning and visual storytelling.</p>
            <p>Morphosys AI assists filmmakers and writers — it does not independently write complete screenplays.</p>
            <p>AI features are available with a Screenwriting + AI subscription and applicable AI credits.</p>
            <Button onClick={() => setSubscriptionOpen(true)}>View AI Subscription</Button>
            <Modal open={subscriptionOpen} onCancel={() => setSubscriptionOpen(false)} footer={null} centered destroyOnClose width="min(560px, calc(100vw - 32px))" title="Screenwriting + AI subscription" closeIcon={<span aria-hidden="true" className="text-2xl leading-none font-bold">×</span>} styles={{ body: { maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' } }}>
              <SubscriptionPreview aiOnly returnTool={toolId} />
            </Modal>
          </div> : tool.custom ? <div className="flex-1 min-w-0 p-4 lg:p-5">{toolId === "production" ? <ProductionElements scripts={scripts} scriptsLoading={scriptsLoading} onCreditsChanged={refreshBalance} /> : toolId === "premium" ? <PremiumVideo scripts={scripts} scriptsLoading={scriptsLoading} fixedScript={fixedScript} beforeGenerate={beforeGenerate} /> : toolId === "scene-studio" ? (canAccessSceneStudio(user) ? <SceneStudio scripts={scripts} scriptsLoading={scriptsLoading} fixedScript={fixedScript} beforeGenerate={beforeGenerate} /> : null) : <AIVideo scripts={scripts} scriptsLoading={scriptsLoading} fixedScript={fixedScript} beforeGenerate={beforeGenerate} />}</div> :
          <div className="flex-1 min-w-0 p-4 lg:p-5 flex flex-col gap-5">
            <div>
              <h3 className="font-bold text-[18px]">{tool.title}{<span className="dashboard-muted font-normal text-[14px]"> · {toolCostText}</span>}</h3>
              <p className="dashboard-muted text-[14px] mt-1">{tool.intro}</p>
            </div>
            {tool.needsScript && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <label className="block font-bold text-[14px] mb-2">Choose script</label>
                <Select aria-label="Choose script" size="large" className="w-full" placeholder="Choose script" showSearch optionFilterProp="label" loading={scriptsLoading}
                  value={scriptId} disabled={!!fixedScript} onChange={setScriptId} notFoundContent={scriptsLoading ? "Loading scripts…" : "No scripts found"}
                  options={scripts.map((s) => ({ value: s._id, label: s.title || "Untitled Script" }))} />
              </div>
              {tool.needsScene && <div className="min-w-0">
                <label className="block font-bold text-[14px] mb-2">Choose scene</label>
                <Select aria-label="Choose scene" size="large" className="w-full" placeholder="Choose scene" disabled={!scriptId} loading={scenesLoading}
                  value={sceneId} onChange={setSceneId} notFoundContent={scenesLoading ? "Loading scenes…" : "No scenes found"}
                  options={scenes.map((s) => ({ value: s.id, label: `${s.index}. ${s.heading}` }))} />
              </div>}
            </div>}
            {tool.id === "script" && scriptId && (pageInfo?.pages
              ? <p data-testid="script-pages" className="text-[14px]"><b>{pageInfo.pages} {pageInfo.pages === 1 ? "page" : "pages"}</b> · {pageInfo.category} · <b>{pageInfo.credits} credits</b></p>
              : <p className="dashboard-muted text-[14px]">{pageInfo ? pageInfo.message : "Checking page count…"}</p>)}
            {tool.id === "storyboard" && regenFrom && <div role="region" aria-label="Regenerate storyboard" className="rounded-xl border p-4 text-[14px]" style={{ borderColor: "var(--dashboard-primary)", background: "var(--dashboard-tint)" }}>
              <p className="font-bold">Regenerating storyboard{regenFrom.sceneNumber ? ` · Scene ${regenFrom.sceneNumber}` : ""}{regenFrom.sceneHeading ? ` ${regenFrom.sceneHeading}` : ""}</p>
              <p className="mt-1">The saved scene, visual style, wardrobe and shot settings are loaded below — adjust anything before regenerating. Current character descriptions from Characters are used. All four frames are created again as a new version; the previous version stays saved.</p>
              <p className="mt-1 font-bold" data-testid="regen-cost">Cost: {tool.cost} AI credits per regeneration.</p>
              {regenNotice && <p role="status" className="mt-1 text-red-600">{regenNotice}</p>}
              <Button type="link" className="px-0 mt-1" onClick={cancelRegenerate}>Cancel regeneration</Button>
            </div>}
            {tool.id === "storyboard" && <AspectRatioSelect value={aspectRatio} onChange={setAspectRatio} legacy={!!regenFrom && aspectRatio === "3:2"} />}
            {tool.id === "storyboard" && <StoryboardSetup />}
            {tool.id === "storyboard" && scriptId && sceneId && <details key={regenFrom ? "regen" : "new"} open={regenFrom && wardrobe ? true : undefined} className="rounded-xl border p-4 text-[14px]" style={{ borderColor: "var(--dashboard-border)" }}>
              <summary className="font-bold cursor-pointer">Character Consistency</summary>
              <p className="mt-3 mb-3">To keep your characters looking consistent across scenes, make sure their descriptions are completed in the Characters section.</p>
              <div className="flex flex-wrap gap-2">
                <Button className="rounded-full font-bold" onClick={() => onCharacters ? onCharacters() : navigate(`/scripts/${scriptId}/characters`)}>Go to Characters</Button>
                <Button className="rounded-full font-bold" onClick={(event) => { const reminder = event.currentTarget.closest("details"); if (reminder) reminder.open = false }}>Continue</Button>
              </div>
              <label htmlFor="sb-wardrobe" className="block font-bold mt-4 mb-2">Scene Appearance / Wardrobe (optional)</label>
              <Input.TextArea id="sb-wardrobe" rows={3} maxLength={2000} value={wardrobe} onChange={(e) => setWardrobe(e.target.value)} placeholder={"ANGEL: Black evening dress, silver earrings, hair tied back.\nJIMMY: Dark suit, white shirt, loosened tie."} />
              <p className="dashboard-muted text-[13px] mt-2">Used for this scene only. Saved character descriptions are not changed.</p>
            </details>}
            {tool.id === "storyboard" && scriptId && sceneId && <CustomiseShots shots={shots} onChange={setShots} Select={Select} Input={Input} />}
            <div>
              <div className="font-bold text-[14px] mb-2">{tool.optionsLabel || "Focus"}</div>
              <div className="flex flex-wrap gap-2">{tool.options.map((opt) => chip(opt, picked.includes(opt), () => toggle(opt)))}</div>
            </div>
            {tool.id !== "storyboard" && <div>
              <label htmlFor="ai-notes" className="block font-bold text-[14px] mb-2">Notes for the AI{tool.notesRequired ? "" : " (optional)"}</label>
              <Input.TextArea id="ai-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={tool.notesRequired ? tool.notesHint : "Add anything the AI should keep in mind"} aria-required={!!tool.notesRequired} />
              {tool.notesRequired && !notes.trim() && <p className="dashboard-muted text-[13px] mt-2">{tool.notesHint}</p>}
            </div>}
            <div className="flex flex-wrap items-center gap-3">
              <Button className="dashboard-primary min-h-11 h-auto max-w-full whitespace-normal py-2 rounded-full px-5 font-bold" disabled={!ready} loading={running} onClick={runTool}>{running ? "Working…" : (tool.id === "storyboard" && regenFrom ? "Regenerate Storyboard" : (tool.actionLabel || `Run ${tool.title}`)) + ` · ${toolCostText}`}</Button>
              {tool.id === "storyboard" && <StoryboardGuidelinesLink onClick={() => setGuidelinesOpen(true)} />}
              {!tool.connected && <span className="dashboard-badge text-[11px] font-bold uppercase rounded-full px-2 py-1">Coming soon</span>}
              {result && !running && <Button type="link" className="px-0" onClick={() => setStage("result")}>View last result</Button>}
            </div>
            {storyboardUpgradeRequired && <div className="flex flex-col items-start gap-3 text-[14px]">
              <p>Storyboard is available with Screenwriting + AI. Upgrade your subscription to create storyboards.</p>
              <Button className="dashboard-primary font-bold" onClick={() => navigate('/home', { state: { section: 'profile', profileSection: 'subscription' } })}>Upgrade to AI</Button>
            </div>}
            {runError && (tool.id === "storyboard" ? <StoryboardFailureNotice message={runError} onGuidelines={() => setGuidelinesOpen(true)} /> : <p role="alert" className="text-red-600 text-[14px] -mt-2">{runError}</p>)}
            {!storyboardUpgradeRequired && creditMessage && <div className="flex flex-wrap items-center gap-2 text-[13px] dashboard-muted"><span role="status">{creditMessage}</span>{!balance && <Button size="small" onClick={refreshBalance}>Retry</Button>}</div>}
            {tool.id === "storyboard" && <SavedStoryboards key={`${savedKey}-${scriptId || 'all'}`} scriptId={scriptId} onOpen={openSaved} openingId={openingId} />}
            {running && <p className="dashboard-muted text-[13px] -mt-2">{toolId === "script" ? "Reading the whole script — this can take a minute." : "This usually takes a few seconds."}</p>}
          </div>}
        </div>
      )}
      <StoryboardContentGuidelines open={guidelinesOpen} onClose={() => setGuidelinesOpen(false)} />
    </div>
  )
}

export default AIWorkspace
